import { Button, Table } from 'antd'
import { ColumnsType } from 'antd/es/table/interface'
import { Breakpoint } from 'antd/lib/_util/responsiveObserver'
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import {
  usePostserviceTemplateTableQuery,
  UuidFilter,
} from '../../../graphql/generated'
import { useAppSelector } from '../../../redux/hooks'
import { stringSorter, utcDateSorter } from '../../utils/generalTableHelpers'
import { tablePagination } from '../../utils/layoutConst'
import { postServiceLetterPath } from '../../utils/paths'
import { Min } from '../DateFormat/DateFormat'
import { PostServiceTemplateTableAction } from '../PostServiceTemplateTableAction/PostServiceTemplateTableAction'
import { TableSkeleton } from '../TableSkeleton/TableSkeleton'

export interface PostServiceTemplateTableItem {
  createdAt: Date
  description?: string
  name: string
  id: string
  content: string
}

export interface PostServiceTemplateTableProps {
  idFilter?: UuidFilter
}

const tableId = 'postServiceTemplate-table'

export const PostServiceTemplateTable: FunctionComponent = () => {
  const tenantId = useAppSelector(state => state.myProfile.tenantId)

  const [dataSource, setDataSource] = useState<PostServiceTemplateTableItem[]>(
    []
  )

  const [queryResult, refetch] = usePostserviceTemplateTableQuery({
    variables: { accountId: tenantId },
  })

  const locale = useMemo(
    () => ({
      emptyText: queryResult.fetching ? (
        <TableSkeleton rows={10} />
      ) : (
        <div className="my-8">
          <div className="mb-2 text-gray-400">
            Zatím nemáte vytvořené žádné šablony pro hromadnou poštu.
          </div>
          <Link to={postServiceLetterPath()}>
            <Button type="primary">Vytvořit první šablonu dopisu</Button>
          </Link>
        </div>
      ),
    }),

    [queryResult.fetching]
  )

  useEffect(() => {
    if (queryResult.data?.allPostserviceTemplates?.nodes?.[0]?.id) {
      setDataSource(
        queryResult.data?.allPostserviceTemplates
          ?.nodes as PostServiceTemplateTableItem[]
      )
    }
  }, [queryResult.data?.allPostserviceTemplates?.nodes])

  const columns = useMemo<ColumnsType<PostServiceTemplateTableItem>>(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        responsive: ['' as Breakpoint],
      },
      {
        title: 'Vytvořeno',
        dataIndex: 'createdAt',
        render: createdAt => <Min>{createdAt}</Min>,
        defaultSortOrder: 'descend',
        className: 'w-36',
        sorter: utcDateSorter('createdAt'),
      },
      {
        title: 'Název',
        dataIndex: 'name',
        className: 'w-1/5',
        sorter: stringSorter('name'),
      },
      {
        title: 'Popis',
        dataIndex: 'description',
        ellipsis: true,
        sorter: stringSorter('description'),
      },
      {
        title: 'Akce',
        dataIndex: 'action',
        className: 'w-48',
        render: (_, record) => {
          return (
            <PostServiceTemplateTableAction
              reexecuteQuery={refetch}
              record={record}
            />
          )
        },
      },
    ],
    [refetch]
  )

  return (
    <Table
      rowKey="id"
      size="small"
      locale={locale}
      id={tableId}
      dataSource={dataSource}
      columns={columns}
      loading={queryResult.fetching}
      bordered
      pagination={tablePagination}
    />
  )
}
